// Note Expander v2, the Playground's pure logic (PR 5b, O192; DESIGN §7.5), kept out of the
// component so tests/unit/v3/note-expander-v2-playground-lib.test.js drives it directly.
//
//   The server runs each column as an ordinary pipeline run and refuses what it should: an
//   unknown model, more than four, a style the user has not approved. What lives here is the
//   form's rules (up to four models; "Standard vs your style" uses the first), the request body,
//   and each column in words.

const MAX_MODELS = 4;
const PROMPTS = Object.freeze([
  ['mine', 'Your style'], ['standard', 'Standard'], ['compare', 'Standard vs your style'],
]);
const PROMPT_STYLE_WORDS = Object.freeze({ mine: 'Your style', standard: 'Standard prompt' });

// Tick or untick a model; a fifth tick is ignored (the server refuses it too).
function toggleModel(selected, alias, max = MAX_MODELS) {
  const list = selected || [];
  if (list.includes(alias)) return list.filter((a) => a !== alias);
  return list.length >= max ? list : list.concat(alias);
}

// POST /v2/playground. The client request id is fresh per Run press and kept for its retries.
function playBody({ draftId, mode, prompt, models, clientRequestId }) {
  return { draftId: String(draftId), mode, prompt, models: [...(models || [])], clientRequestId };
}

// A comparison is done when every column has finished.
const FINISHED = Object.freeze(['ready', 'needs_attention', 'failed', 'superseded']);
function comparisonSettled(columns) {
  return (columns || []).length > 0 && columns.every((c) => FINISHED.includes(c.status));
}

// One column's facts in words: time, length, and Maximal's highlights or Grounded's suggestions,
// with what the safety checks cut.
function columnFacts(col) {
  if (!col || col.status !== 'ready') return [];
  const out = [];
  if (col.seconds != null) out.push(`${col.seconds} s`);
  if (col.length != null) out.push(`${Number(col.length).toLocaleString('en-US')} characters`);
  if (col.mode === 'maximal' && col.highlights != null) out.push(`${col.highlights} highlight${col.highlights === 1 ? '' : 's'}`);
  if (col.mode === 'grounded' && col.suggestions != null) out.push(`${col.suggestions} suggestion${col.suggestions === 1 ? '' : 's'} set aside`);
  if (col.cuts) out.push(`${col.cuts} cut by the safety checks`);
  return out;
}

// A column's state: running, ready, or why not, in the server's words (COPY.run).
function columnState(col, copy) {
  if (!col) return { kind: 'none', words: '' };
  if (col.status === 'queued' || col.status === 'running') return { kind: 'running', words: 'Running…' };
  if (col.status === 'ready') return col.purged ? { kind: 'done', words: 'The text was removed after 90 days.' } : { kind: 'ready', words: '' };
  const why = copy && copy.run && col.statusReason && copy.run[col.statusReason];
  return { kind: 'failed', words: why || (col.status === 'failed' ? 'This column failed.' : 'Nothing usable came back.') };
}

// The model's reasoning (owner 2026-10-21, Playground only): the pipeline's stages in words, and
// one entry's heading. A stage the page does not know shows as itself.
const STAGE_WORDS = Object.freeze({ extract: 'Reading the draft', synthesize: 'Writing the note', check: 'Checking it', style: 'Style' });
const REASONING_CAPTION = "The model's own reasoning, kept with this comparison for 90 days. It is never part of the note.";

function reasoningEntryTitle(entry) {
  if (!entry) return '';
  const parts = [STAGE_WORDS[entry.stage] || String(entry.stage || '')];
  if (entry.call > 1) parts.push(`try ${entry.call}`);
  parts.push(`${Number(entry.chars || 0).toLocaleString('en-US')} characters${entry.truncated ? ', shortened' : ''}`);
  return parts.join(' · ');
}

// A refused promote ("Use this one") in words.
function promoteError(err, copy) {
  const reason = err && err.body && err.body.reason;
  const group = (g) => copy && copy[g] && reason && copy[g][reason];
  if (reason) return group('playground') || group('request') || group('accept') || 'This column cannot be used now.';
  if (err && err.status === 404) return 'This column is no longer available.';
  return 'This column could not be opened. Try again.';
}

module.exports = {
  MAX_MODELS, PROMPTS, PROMPT_STYLE_WORDS, STAGE_WORDS, REASONING_CAPTION,
  toggleModel, playBody, comparisonSettled, columnFacts, columnState, promoteError, reasoningEntryTitle,
};
